// Angebot (Belegart 'quote', AN-) und Auftrag (Belegart 'sales_order', AB-). Die Standard-Verträge kommen aus der
// Belegfabrik; dazu Rabattfreigabe, Kreditlimit, Reservierung mit sichtbarem Rückstand und die Auftragsübersicht.
//
//   Angebot   Entwurf → Offen → Teilweise beauftragt → Beauftragt · Abgelehnt · Abgelaufen
//   Auftrag   Entwurf | Eingegangen → (Rabatt zur Freigabe → Rabatt freigegeben) → (Kreditsperre) → Bestätigt
//             → Teilgeliefert → Geliefert → Abgeschlossen
//
// „Festschreiben“ heißt beim Auftrag „bestätigen“: Der Bestand am liefernden Standort wird reserviert; was nicht frei
// ist, bleibt als Rückstand sichtbar. Mengen der Zeilen stehen in Tausendstel des Gebindes der Zeile, Preise in Cent
// je Preiseinheit in der Basis des Belegs (netto bei Geschäftskunden, brutto bei Privatkunden).
import { PERMISSION_COSTS_READ, defineContract } from '@shared/contract'
import { DOCUMENT_SORT_KEYS, defineDocumentContracts, zDocumentListRow, zDocumentView } from '@shared/document'
import { zId } from '@shared/ids'
import { listInput, listOutput } from '@shared/list'
import { z } from 'zod'
import { ORDER_KINDS, ORDER_TYPE, QUOTE_TYPE, SALES_CHANNELS } from '../lib/labels'
import { zCheckoutIssue } from './checkout'
import { PHONE_ORDER_HINTS } from '../lib/phone'
import { zCustomerRef, zDay, zDocumentRef, zLocationRef, zPerson, zReason } from './common'

const QUOTE_PERMISSIONS = { read: 'sales.quote.read', write: 'sales.quote.write', post: 'sales.quote.write', reverse: 'sales.quote.write' } as const
const ORDER_PERMISSIONS = { read: 'sales.order.read', write: 'sales.order.write', post: 'sales.order.write', reverse: 'sales.order.write' } as const
const COSTS = { 'lines.*.costCents': PERMISSION_COSTS_READ } as const

export const quote = defineDocumentContracts({
  module: 'sales',
  entity: 'quote',
  documentType: QUOTE_TYPE,
  title: 'Angebot',
  titlePlural: 'Angebote',
  permissions: QUOTE_PERMISSIONS,
  financial: false,
  keywords: ['Angebot', 'Offerte', 'Kostenvoranschlag', 'Festangebot', 'AN'],
})

export const quoteGet = quote.get
export const quoteList = quote.list
export const quoteSaveDraft = quote.saveDraft
/** „Angebot abgeben“: vergibt die Nummer; danach lässt es sich als Fassung ändern und in Aufträge übernehmen */
export const quotePost = quote.post
/** Angebot zurückziehen */
export const quoteReverse = quote.reverse
/** „Abgelehnt“ (mit Grund) und „Abgelaufen“ */
export const quoteTransition = quote.transition
export const quotePrint = quote.print
export const quoteDiscard = quote.discard

export const order = defineDocumentContracts({
  module: 'sales',
  entity: 'order',
  documentType: ORDER_TYPE,
  title: 'Auftrag',
  titlePlural: 'Aufträge',
  permissions: ORDER_PERMISSIONS,
  derivesFrom: [QUOTE_TYPE],
  keywords: ['Auftrag', 'Kundenauftrag', 'Bestellung des Kunden', 'Auftragsbestätigung', 'AB', 'Lieferauftrag'],
})

export const orderGet = order.get
export const orderList = order.list
export const orderSaveDraft = order.saveDraft
/**
 * „Auftrag bestätigen“ – reserviert den Bestand. Liegt ein Rabatt über der Grenze der Person, geht der Auftrag
 * stattdessen zur Freigabe; über dem Kreditlimit in die Kreditsperre (der Beleg trägt dann diesen Zustand).
 */
export const orderPost = order.post
/** Auftrag abbrechen (solange nichts geliefert ist): Reservierungen werden frei */
export const orderReverse = order.reverse
/** „Rest schließen“ (bestätigt/teilgeliefert → abgeschlossen) und „Abschließen“ (geliefert → abgeschlossen) */
export const orderTransition = order.transition
/** Auftragsbestätigung als A4-Beleg */
export const orderPrint = order.print
export const orderDiscard = order.discard
/** Auftrag aus einem Angebot – ganz oder in Teilmengen */
export const orderDerive = order.derive

export const orderApprove = defineContract({
  name: 'sales.order.approve',
  title: 'Rabatt eines Auftrags freigeben',
  description: 'Gibt den Rabatt eines Auftrags frei, der über der Grenze der erfassenden Person liegt, und bestätigt den Auftrag.',
  llm: {
    description:
      'Gibt einen Auftrag im Zustand „Rabatt zur Freigabe“ frei. Erlaubt nur, wessen Rabattgrenze (maxDiscountBp) den ' +
      'höchsten Nachlass des Auftrags deckt. Danach wird der Auftrag bestätigt und reserviert – außer das Kreditlimit ' +
      'ist überschritten: Dann steht er in der Kreditsperre (sales.order.releaseCredit). Zum Zurückgeben mit Grund: ' +
      'sales.order.reject.',
    keywords: ['Rabatt freigeben', 'Freigabe', 'Nachlass genehmigen', 'Sonderpreis'],
  },
  input: z.strictObject({ id: zId }),
  output: zDocumentView,
  permission: 'sales.discount.grant',
  risk: 'financial',
  scope: 'record',
  fieldPermissions: COSTS,
})

export const orderReject = defineContract({
  name: 'sales.order.reject',
  title: 'Auftrag zurückgeben',
  description: 'Gibt einen Auftrag, dessen Rabatt auf Freigabe wartet, mit Grund an den Vertrieb zurück.',
  llm: {
    description: 'Lehnt die Rabattfreigabe eines Auftrags ab und gibt ihn mit Grund zurück: Er ist wieder bearbeitbar, bestätigt und reserviert wird nichts. Nur im Zustand „Rabatt zur Freigabe“.',
    keywords: ['Rabatt ablehnen', 'Auftrag zurückgeben', 'Freigabe ablehnen'],
  },
  input: z.strictObject({ id: zId, reason: zReason }),
  output: zDocumentView,
  permission: 'sales.discount.grant',
  risk: 'write',
  scope: 'record',
  fieldPermissions: COSTS,
})

export const orderWithdraw = defineContract({
  name: 'sales.order.withdraw',
  title: 'Auftrag zurück in Bearbeitung nehmen',
  description: 'Holt einen wartenden Auftrag (Rabattfreigabe, Kreditsperre) zurück in die Bearbeitung.',
  llm: {
    description:
      'Nimmt einen noch nicht bestätigten Auftrag aus „Rabatt zur Freigabe“, „Rabatt freigegeben“ oder ' +
      '„Kreditsperre“ zurück in die Bearbeitung, etwa um Mengen oder Preise zu ändern. Eine erteilte Freigabe verfällt.',
    keywords: ['zurück in Bearbeitung', 'Auftrag ändern', 'Freigabe zurückziehen'],
  },
  input: z.strictObject({ id: zId, reason: z.string().trim().max(500).nullish() }),
  output: zDocumentView,
  permission: ORDER_PERMISSIONS.write,
  risk: 'write',
  scope: 'record',
  fieldPermissions: COSTS,
})

export const orderReleaseCredit = defineContract({
  name: 'sales.order.releaseCredit',
  title: 'Auftrag trotz Kreditlimit freigeben',
  description: 'Bestätigt einen Auftrag in der Kreditsperre – die Überschreitung steht mit Zahlen im Protokoll.',
  llm: {
    description:
      'Gibt einen Auftrag im Zustand „Kreditsperre“ frei und bestätigt ihn (reserviert den Bestand), obwohl offene ' +
      'Posten, unberechnete Lieferungen und bestätigte Aufträge des Kunden zusammen das Kreditlimit überschreiten. ' +
      'Braucht einen Grund. Die Zahlen liefert sales.order.detail; das Limit selbst ändert die Kundenakte.',
    keywords: ['Kreditlimit freigeben', 'Kreditsperre aufheben', 'trotz Limit liefern'],
  },
  input: z.strictObject({ id: zId, reason: zReason }),
  output: zDocumentView,
  permission: 'sales.creditLimit.override',
  risk: 'financial',
  scope: 'record',
  fieldPermissions: COSTS,
})

export const orderReserve = defineContract({
  name: 'sales.order.reserve',
  title: 'Rückstand eines Auftrags nachreservieren',
  description: 'Reserviert für einen bestätigten Auftrag nach, was inzwischen am Standort frei geworden ist.',
  llm: {
    description:
      'Versucht für einen bestätigten oder teilgelieferten Auftrag, den Rückstand zu reservieren (etwa nach einem ' +
      'Wareneingang). Reserviert wird, was frei verfügbar ist; der Rest bleibt Rückstand. Meldet je Zeile die neu ' +
      'reservierte Menge in Tausendstel der Basiseinheit.',
    keywords: ['nachreservieren', 'Rückstand auflösen', 'reservieren', 'Ware ist da'],
  },
  input: z.strictObject({ id: zId }),
  output: z.object({
    id: zId,
    /** neu reservierte Menge in Tausendstel der Basiseinheit, über alle Zeilen */
    reservedQty: z.int(),
    /** verbleibender Rückstand in Tausendstel der Basiseinheit */
    backorderQty: z.int(),
    message: z.string(),
  }),
  permission: ORDER_PERMISSIONS.write,
  risk: 'write',
  scope: 'record',
})

export const zCreditFigures = z.object({
  /** null = kein Limit hinterlegt */
  limitCents: z.int().nullable(),
  openItemsCents: z.int(),
  /** geliefert, noch nicht berechnet */
  unbilledCents: z.int(),
  /** bestätigte, noch nicht gelieferte Aufträge (ohne den betrachteten) */
  ordersCents: z.int(),
  thisOrderCents: z.int(),
  exposureCents: z.int(),
  availableCents: z.int().nullable(),
  exceededCents: z.int(),
  /** über dem Limit */
  blocked: z.boolean(),
  overdueCents: z.int(),
  /** fertiger Satz mit den Zahlen */
  message: z.string(),
})

export const creditStatus = defineContract({
  name: 'sales.credit.status',
  title: 'Kreditlimit eines Kunden prüfen',
  description: 'Offene Posten, unberechnete Lieferungen und bestätigte Aufträge eines Kunden gegen sein Kreditlimit.',
  llm: {
    description:
      'Rechnet für einen Kunden die Auslastung seines Kreditlimits: offene Posten + geliefert und noch nicht berechnet + ' +
      'bestätigte, noch nicht gelieferte Aufträge (+ optional ein zusätzlicher Betrag in Cent brutto) gegen das Limit. ' +
      'Verwenden für „Kann Bella Isar noch bestellen?“. Ändert nichts.',
    keywords: ['Kreditlimit', 'Kreditrahmen', 'offene Posten', 'Limit erreicht', 'darf der Kunde bestellen'],
  },
  input: z.strictObject({ partnerId: zId, additionalCents: z.int().min(0).max(1_000_000_000).default(0) }),
  output: zCreditFigures.extend({ customer: zCustomerRef }),
  permission: ORDER_PERMISSIONS.read,
  risk: 'read',
  scope: 'any',
})

export const zOrderLineState = z.object({
  lineId: zId,
  pos: z.int(),
  itemId: zId.nullable(),
  sku: z.string().nullable(),
  description: z.string().nullable(),
  unitId: zId.nullable(),
  /** Kurzform des Gebindes: „Ka“ */
  unit: z.string().nullable(),
  /** Basismenge je Gebinde der Zeile in Tausendstel */
  unitBaseQty: z.int(),
  /** alle Mengen im Gebinde der Zeile, Tausendstel (1 Kasten = 1000) */
  orderedQty: z.int(),
  shippedQty: z.int(),
  /** bestellt − geliefert */
  openQty: z.int(),
  reservedQty: z.int(),
  /** offen und nicht reserviert */
  backorderQty: z.int(),
  /** jetzt frei verfügbar am liefernden Standort (ohne Reserviertes und Gesperrtes) */
  availableQty: z.int(),
  /** Mindestalter des Artikels (0, 16, 18) */
  minAge: z.int(),
  /** höchster Nachlass der Zeile gegenüber der Preisfindung in Basispunkten */
  discountBp: z.int(),
})

export const orderDetail = defineContract({
  name: 'sales.order.detail',
  title: 'Stand eines Auftrags anzeigen',
  description: 'Rabattfreigabe, Kreditlimit, Reservierung und Rückstand je Zeile, Lieferscheine und Herkunft eines Auftrags.',
  llm: {
    description:
      'Liefert zu einem Auftrag, was der Beleg selbst nicht zeigt: Herkunft (Vertrieb, Telefon, Bestell-App), ' +
      'Zeitfenster und Fahrzeug, Stand der Rabattfreigabe (höchster Nachlass, Grenze des Aufrufers, ob er freigeben ' +
      'darf), die Kreditlimit-Rechnung des Kunden mit allen Summanden, je Zeile bestellte, gelieferte, reservierte ' +
      'und rückständige Menge (im Gebinde der Zeile, Tausendstel) samt dem, was jetzt frei wäre, den Hinweis zur ' +
      'Alterskontrolle und die Lieferscheine dazu. Verwenden für „Was ist von AB-… noch offen?“ oder „Warum ist der ' +
      'Auftrag gesperrt?“. Den Beleg selbst liefert sales.order.get.',
    keywords: ['Rückstand', 'reserviert', 'Lieferstand', 'Kreditsperre', 'Rabattfreigabe', 'Herkunft', 'Zeitfenster'],
  },
  input: z.strictObject({ id: zId }),
  output: z.object({
    id: zId,
    number: z.string().nullable(),
    status: z.string(),
    statusLabel: z.string(),
    posted: z.boolean(),
    kind: z.enum(ORDER_KINDS),
    channel: z.enum(SALES_CHANNELS).nullable(),
    channelLabel: z.string().nullable(),
    /** Bestellnummer des Kanals: „AL-24851“ */
    channelNumber: z.string().nullable(),
    customer: zCustomerRef.nullable(),
    location: zLocationRef,
    deliveryDate: z.string().nullable(),
    /** „08:00–12:00 Uhr“ */
    timeWindow: z.string().nullable(),
    vehicle: zLocationRef.nullable(),
    pickingNote: z.string().nullable(),
    driverNote: z.string().nullable(),
    discount: z.object({
      /** höchster Nachlass des Auftrags in Basispunkten */
      maxBp: z.int(),
      /** Rabattgrenze des Aufrufers in Basispunkten; null = unbegrenzt; 0 = kein Rabattrecht */
      limitBp: z.int().nullable(),
      /** würde der Aufrufer jetzt bestätigen, ginge der Auftrag zur Freigabe */
      required: z.boolean(),
      pending: z.boolean(),
      submittedBy: zPerson.nullable(),
      submittedAt: z.date().nullable(),
      approvedBy: zPerson.nullable(),
      returnReason: z.string().nullable(),
      canApprove: z.boolean(),
      /** warum der Aufrufer nicht freigeben darf – nur wenn er das Recht grundsätzlich hat */
      approveHint: z.string().nullable(),
    }),
    credit: zCreditFigures.extend({
      /** der Auftrag steht in der Kreditsperre */
      onHold: z.boolean(),
      canRelease: z.boolean(),
      releasedBy: zPerson.nullable(),
    }).nullable(),
    /** wartend (Freigabe, Kreditsperre) und der Aufrufer darf ihn zurück in die Bearbeitung nehmen */
    canWithdraw: z.boolean(),
    lines: z.array(zOrderLineState),
    /** „3 Positionen im Rückstand: 12 Ka Augustiner Hell · …“ oder null */
    backorderText: z.string().nullable(),
    canReserve: z.boolean(),
    /** es gibt offene Mengen und der Aufrufer darf am Standort kommissionieren */
    canPick: z.boolean(),
    /** höchstes Mindestalter der Artikel und der Hinweis dazu */
    minAge: z.int(),
    ageNotice: z.string().nullable(),
    deliveries: z.array(zDocumentRef),
    quotes: z.array(zDocumentRef),
    /** Prüfung der Lieferregeln bei einer Bestellung der Bestell-App (docs/integration/bestell-app.md §19); null = nicht geprüft */
    checkout: z
      .object({
        /** Prüfhinweise, leer = alles stimmt */
        issues: z.array(zCheckoutIssue),
        zoneName: z.string().nullable(),
        couponCode: z.string().nullable(),
        /** Bestellzeitpunkt, gegen den der Bestellschluss geprüft wurde */
        placedAt: z.string().nullable(),
        /** Telefonbestellung (§20 S1): Gebühr bzw. Tragservice, die das Personal abweichend von den Regeln gesetzt hat */
        overrides: z.array(z.object({ what: z.enum(['delivery', 'carry']), setCents: z.int(), ruleCents: z.int() })),
      })
      .nullable(),
    /** Abholung (§20 S2): Zustand des Abholcodes; der Code selbst nur mit dem Recht „Abholungen prüfen und ausgeben“. null bei Lieferung */
    pickup: z
      .object({
        hasCode: z.boolean(),
        code: z.string().nullable(),
        source: z.enum(['app', 'merxo']).nullable(),
        pickedUpAt: z.date().nullable(),
        pickedUpBy: z.string().nullable(),
      })
      .nullable(),
    /** Telefon-Bestellung des Telefonassistenten (docs/integration/telefonassistent.md §16); null bzw. fehlend bei anderer Herkunft */
    phoneOrder: z
      .object({
        accessId: z.string().nullable(),
        callbackPhone: z.string().nullable(),
        phoneSource: z.enum(['caller_id', 'other']).nullable(),
        phoneConfirmed: z.boolean(),
        ageConfirmed: z.boolean(),
        callRef: z.string().nullable(),
        placedAt: z.string().nullable(),
        late: z.boolean(),
        contactName: z.string().nullable(),
        hints: z.array(z.object({ code: z.enum(PHONE_ORDER_HINTS), message: z.string() })),
        /** Rückruf vor dem Bestätigen nötig (§16.12) und – falls vermerkt – wann und von wem */
        callbackRequired: z.boolean().optional(),
        callbackAt: z.date().nullable().optional(),
        callbackBy: z.string().nullable().optional(),
      })
      .nullable()
      .optional(),
  }),
  permission: ORDER_PERMISSIONS.read,
  risk: 'read',
  scope: 'record',
})

export const ORDER_VIEWS = ['all', 'unconfirmed', 'open', 'backorder'] as const

export const zOrderOverviewRow = zDocumentListRow.extend({
  locationCode: z.string(),
  deliveryDate: z.string().nullable(),
  timeWindow: z.string().nullable(),
  kind: z.enum(ORDER_KINDS),
  channel: z.enum(SALES_CHANNELS).nullable(),
  channelLabel: z.string().nullable(),
  vehicleCode: z.string().nullable(),
  /** Zeilen mit offener (noch nicht gelieferter) Menge */
  openLineCount: z.int(),
  /** Zeilen mit Rückstand (offen und nicht reserviert) */
  backorderLineCount: z.int(),
  /** „12 Ka Augustiner Hell · 2 Fa Hell KEG“ */
  backorderText: z.string().nullable(),
  /** Zahl der Prüfhinweise der Lieferregeln (Bestellung der Bestell-App) */
  checkoutIssueCount: z.int(),
  /** Telefon-Bestellung des Telefonassistenten mit nicht bestätigter Rückrufnummer (telefonassistent.md §16) */
  phoneUnconfirmed: z.boolean().optional(),
})

export const orderOverview = defineContract({
  name: 'sales.order.overview',
  title: 'Auftragsübersicht anzeigen',
  description: 'Aufträge mit Liefertermin, Herkunft und Rückständen – für Vertrieb und Lager.',
  llm: {
    description:
      'Listet Aufträge an den Standorten, die der Nutzer sehen darf, mit Liefertermin, Zeitfenster, Herkunft ' +
      '(Bestell-App, Telefon, Vertrieb), Fahrzeug und Rückstand (offene, nicht reservierte Mengen). `view`: ' +
      'unconfirmed = noch nicht bestätigt (Entwurf, eingegangen, Freigabe, Kreditsperre) · open = bestätigt und nicht ' +
      'vollständig geliefert · backorder = nur Aufträge mit Rückstand. Weitere Filter wie bei Beleglisten. Verwenden ' +
      'für „Welche Aufträge sind im Rückstand?“ oder „Was ist heute eingegangen?“.',
    keywords: ['Auftragsübersicht', 'Rückstand', 'offene Aufträge', 'eingegangen', 'Liefertermin', 'Bestell-App'],
  },
  input: listInput({
    sortBy: DOCUMENT_SORT_KEYS,
    defaultSort: { by: 'documentDate', dir: 'desc' },
    filters: {
      view: z.enum(ORDER_VIEWS).optional(),
      status: z.array(z.string().max(40)).max(20).optional(),
      partnerId: zId.optional(),
      locationIds: z.array(zId).max(200).optional(),
      from: zDay.optional(),
      to: zDay.optional(),
      channel: z.enum(SALES_CHANNELS).optional(),
    },
  }),
  output: listOutput(zOrderOverviewRow),
  permission: ORDER_PERMISSIONS.read,
  risk: 'read',
  scope: 'any',
})

export const orderTodoCount = defineContract({
  name: 'sales.order.todoCount',
  title: 'Offene Vorgänge im Verkauf zählen',
  description: 'Zählt eingegangene Aufträge, wartende Freigaben und Aufträge mit Rückstand – für Navigation und Startseite.',
  llm: {
    description: 'Zählt an den Standorten des Nutzers: eingegangene, noch nicht bestätigte Aufträge, Aufträge in Rabattfreigabe oder Kreditsperre und bestätigte Aufträge mit Rückstand. Für Details sales.order.overview.',
    keywords: ['wie viele Aufträge', 'offene Aufträge zählen'],
  },
  input: z.strictObject({}),
  output: z.object({ count: z.int(), received: z.int(), waiting: z.int(), backorder: z.int(), open: z.int() }),
  permission: ORDER_PERMISSIONS.read,
  risk: 'read',
  scope: 'any',
})
